import { useEffect, useRef, useCallback } from 'react';
import {
  createChart,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  createSeriesMarkers,
} from 'lightweight-charts';
import {
  emaSafe, atrTrailingStop, linRegChannel, computeFibLevels,
  mapSeriesToVisible, buildCPRSegments, scoreBarColor, chartPaneMargins,
  resolvePartRow, buildPartHoverHtml,
} from '../../utils/eodChartMath';
import {
  CHART_INTERACTION_OPTS,
  CHART_TIME_SCALE_OPTS,
  attachChartInteraction,
  zoomLogical,
  panLogical,
  fitChart,
  resetPriceScale,
} from '../../utils/chartInteraction';

/** Read a CSS variable from :root, with a fallback. */
function getCSSVar(name, fallback) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch (_) { return fallback; }
}

const FIB_COLORS = [
  'rgba(41,182,246,0.95)', 'rgba(255,152,0,0.95)', 'rgba(171,71,188,0.95)',
  'rgba(255,213,79,0.95)', 'rgba(76,175,80,0.95)', 'rgba(239,83,80,0.95)',
  'rgba(120,144,156,0.95)',
];

const EMA_CFG = [
  { key: 'ema20', period: 20, color: '#1E88E5', width: 1.4 },
  { key: 'ema50', period: 50, color: '#E53935', width: 1.3 },
  { key: 'ema100', period: 100, color: '#2E7D32', width: 1.3 },
  { key: 'ema200', period: 200, color: '#888888', width: 1.8 },
];

const MARKER_SIZE_SCALE = 0.4; // 60% smaller than original defaults
const REF_BAR_SPACING = 6;
const MOBILE_BREAKPOINT = 768;
const MARKER_BASE = {
  fo3d: 1.5,
  fo: 1,
  eq3d: 1.5,
  eq: 0.8,
};

function markerSizeFactor(barSpacing, containerWidth) {
  const barFactor = Math.max(0.5, Math.min(1.75, barSpacing / REF_BAR_SPACING));
  const mobileFactor = containerWidth < MOBILE_BREAKPOINT ? 0.82 : 1;
  return MARKER_SIZE_SCALE * barFactor * mobileFactor;
}

function scaledMarkerSize(base, barSpacing, containerWidth, shape = 'circle') {
  const shapeFactor = shape === 'circle' ? 0.92 : 1;
  const size = base * markerSizeFactor(barSpacing, containerWidth) * shapeFactor;
  return Math.max(0.18, Math.min(1.1, size));
}

/** Purple = inverted triangle; green 3D = triangle; green regular = dot. */
function signalMarkerShape(color) {
  if (color === '#FF00FF') return 'arrowDown';
  if (color === '#00FF41') return 'arrowUp';
  return 'circle';
}

function pushSignalMarker(markers, {
  time, position, color, is3D, barSpacing, containerWidth, eq = false, text = '',
}) {
  const shape = signalMarkerShape(color);
  const sizeBase = is3D
    ? (eq ? MARKER_BASE.eq3d : MARKER_BASE.fo3d)
    : (eq ? MARKER_BASE.eq : MARKER_BASE.fo);
  markers.push({
    time,
    position,
    color,
    shape,
    size: scaledMarkerSize(sizeBase, barSpacing, containerWidth, shape),
    text: text || undefined,
  });
}

/**
 * Participant Fresh Long/Short markers (Client / FII / DII / Pro).
 * One combined marker per direction per day so hover text lists who fired it.
 */
function buildParticipantMarkers(partSignals, barSpacing, containerWidth) {
  const markers = [];
  if (!partSignals?.length) return markers;

  partSignals.forEach(day => {
    const parts = day.participants || [];
    if (!parts.length) return;

    const bulls = parts.filter(p => p.direction === 'bull');
    const bears = parts.filter(p => p.direction === 'bear');

    // No permanent text on markers — participant names show only on hover (OHLC bar).
    if (bulls.length) {
      const is3D = bulls.some(p => p.is3D);
      pushSignalMarker(markers, {
        time: day.date,
        position: 'belowBar',
        color: is3D ? '#00FF41' : '#39FF14',
        is3D,
        barSpacing,
        containerWidth,
      });
    }
    if (bears.length) {
      const is3D = bears.some(p => p.is3D);
      pushSignalMarker(markers, {
        time: day.date,
        position: 'aboveBar',
        color: is3D ? '#FF00FF' : '#EC407A',
        is3D,
        barSpacing,
        containerWidth,
      });
    }
  });

  return markers;
}

function buildSignalMarkers({
  data, toggles, foData, eqData, foReversal, eqReversal,
  partSignals, barSpacing, containerWidth,
}) {
  const markers = [];

  if (foData?.length && toggles.foScore) {
    const foMap = new Map();
    foData.forEach(r => foMap.set(r.date, r.foScore));
    const obLv = foReversal?.obLevel ?? 17;
    const osLv = foReversal?.osLevel ?? 4;
    data.forEach((d, i) => {
      const fs = foMap.get(d.date);
      if (fs == null) return;
      const prev1 = i >= 1 ? foMap.get(data[i - 1]?.date) : null;
      const prev2 = i >= 2 ? foMap.get(data[i - 2]?.date) : null;
      if (fs >= obLv) {
        const is3D = prev1 != null && prev1 >= obLv && prev2 != null && prev2 >= obLv;
        pushSignalMarker(markers, {
          time: d.date, position: 'aboveBar',
          color: is3D ? '#FF00FF' : '#EC407A', is3D,
          barSpacing, containerWidth,
        });
      }
      if (fs <= osLv) {
        const is3D = prev1 != null && prev1 <= osLv && prev2 != null && prev2 <= osLv;
        pushSignalMarker(markers, {
          time: d.date, position: 'belowBar',
          color: is3D ? '#00FF41' : '#39FF14', is3D,
          barSpacing, containerWidth,
        });
      }
    });
  }

  if (eqData?.data?.length && toggles.eqScore) {
    const eqMap = new Map();
    eqData.data.forEach(r => eqMap.set(r.date, r.eqScore));
    const eqMaxS = eqReversal?.maxScore ?? eqData.maxScore ?? 20;
    const eqOB = eqReversal?.obLevel ?? (eqMaxS === 12 ? 10 : 17);
    const eqOS = eqReversal?.osLevel ?? (eqMaxS === 12 ? 3 : 4);
    data.forEach((d, i) => {
      const es = eqMap.get(d.date);
      if (es == null) return;
      const prev1 = i >= 1 ? eqMap.get(data[i - 1]?.date) : null;
      const prev2 = i >= 2 ? eqMap.get(data[i - 2]?.date) : null;
      if (es >= eqOB) {
        const is3D = prev1 != null && prev1 >= eqOB && prev2 != null && prev2 >= eqOB;
        pushSignalMarker(markers, {
          time: d.date, position: 'aboveBar',
          color: is3D ? '#FF00FF' : '#EC407A', is3D,
          barSpacing, containerWidth, eq: true,
        });
      }
      if (es <= eqOS) {
        const is3D = prev1 != null && prev1 <= eqOS && prev2 != null && prev2 <= eqOS;
        pushSignalMarker(markers, {
          time: d.date, position: 'belowBar',
          color: is3D ? '#00FF41' : '#39FF14', is3D,
          barSpacing, containerWidth, eq: true,
        });
      }
    });
  }

  // Participant Fresh Long/Short (Client · FII · DII · Pro)
  if (toggles.partSig && partSignals?.length) {
    markers.push(...buildParticipantMarkers(partSignals, barSpacing, containerWidth));
  }

  return markers;
}

function buildScoreSeries(dates, scores, obLevel, osLevel) {
  return dates.map((date, i) => {
    const v = scores[i];
    if (v == null) return null;
    return {
      time: date,
      value: v,
      color: scoreBarColor(v, i, scores, obLevel, osLevel),
    };
  }).filter(Boolean);
}

/**
 * TradingView Lightweight Charts — EOD candlestick + FO/EQ score panes + overlays.
 */
export default function EodCandleChart({
  data = [],
  fullData = [],
  toggles = {},
  foData = [],
  eqData = [],
  partSignals = [],
  partSeriesByDate = null,
  cprMonthly = [],
  cprWeekly = [],
  monthStarts = [],
  foReversal = null,
  eqReversal = null,
  height = 960,
  onCrosshair = null,
  onHoverIdx = null,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleRef = useRef(null);
  const foScoreRef = useRef(null);
  const eqScoreRef = useRef(null);
  const overlayRefs = useRef([]);
  const priceLineRefs = useRef([]);
  const markersRef = useRef(null);
  const legendRef = useRef(null);
  const zoomBarRef = useRef(null);

  const clearOverlays = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    overlayRefs.current.forEach(s => { try { chart.removeSeries(s); } catch (_) {} });
    overlayRefs.current = [];
    priceLineRefs.current.forEach(pl => { try { candleRef.current?.removePriceLine(pl); } catch (_) {} });
    priceLineRefs.current = [];
    if (markersRef.current) { try { markersRef.current.setMarkers([]); } catch (_) {} }
  }, []);

  const addLine = useCallback((chart, points, opts) => {
    const s = chart.addSeries(LineSeries, {
      color: opts.color,
      lineWidth: opts.width || 1.5,
      lineStyle: opts.dash ? 2 : 0,
      title: opts.title || '',
      priceScaleId: 'right',
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
    });
    s.setData(points.filter(p => p.value != null));
    overlayRefs.current.push(s);
    return s;
  }, []);

  const addSegmentLines = useCallback((chart, segments, dates, fields, color, dash = false) => {
    for (const seg of segments) {
      for (const field of fields) {
        const val = seg[field];
        if (val == null) continue;
        const pts = [];
        for (let i = seg.fi; i <= seg.li && i < dates.length; i++) {
          pts.push({ time: dates[i], value: val });
        }
        if (pts.length >= 2) addLine(chart, pts, { color, width: 1, dash, title: field });
      }
    }
  }, [addLine]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !data.length) return;

    const dates = data.map(d => d.date);
    const H = data.map(d => d.high);
    const L = data.map(d => d.low);
    const C = data.map(d => d.close);
    const n = data.length;

    const fullDates = (fullData.length ? fullData : data).map(d => d.date);
    const fullCloses = (fullData.length ? fullData : data).map(d => d.close);

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }
    clearOverlays();

    const candleGreen = getCSSVar('--candle-green', '#00FF66');
    const candleRed   = getCSSVar('--candle-red', '#FF0055');
    const chartGrid   = getCSSVar('--chart-grid', 'rgba(255,255,255,0.05)');
    const chartText   = getCSSVar('--chart-text', 'rgba(255,255,255,0.45)');
    const borderCol   = getCSSVar('--border-color', 'rgba(255,255,255,0.12)');

    const chart = createChart(el, {
      width: el.clientWidth,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: chartText,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: chartGrid },
        horzLines: { color: chartGrid },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: {
        borderColor: borderCol,
        autoScale: true,
        scaleMargins: { top: 0.05, bottom: 0.05 },
      },
      timeScale: {
        borderColor: borderCol,
        timeVisible: false,
        ...CHART_TIME_SCALE_OPTS,
      },
      ...CHART_INTERACTION_OPTS,
    });
    chartRef.current = chart;

    const detachInteraction = attachChartInteraction(chart, el, {
      zoomStep: 0.14,
      fineZoomStep: 0.06,
      panBars: 4,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: candleGreen,
      downColor: candleRed,
      borderUpColor: candleGreen,
      borderDownColor: candleRed,
      wickUpColor: candleGreen,
      wickDownColor: candleRed,
      priceScaleId: 'right',
    });
    candleRef.current = candleSeries;

    const foMap = new Map();
    foData?.forEach(r => foMap.set(r.date, r.foScore));
    const foScoresArr = data.map(d => foMap.get(d.date) ?? null);

    const eqMap = new Map();
    eqData?.data?.forEach(r => eqMap.set(r.date, r.eqScore));
    const eqScoresArr = data.map(d => eqMap.get(d.date) ?? null);

    const eqMaxScore = eqReversal?.maxScore ?? eqData?.maxScore ?? 20;
    const foOB = foReversal?.obLevel ?? 17;
    const foOS = foReversal?.osLevel ?? 4;
    const eqOB = eqReversal?.obLevel ?? (eqMaxScore === 12 ? 10 : 17);
    const eqOS = eqReversal?.osLevel ?? (eqMaxScore === 12 ? 3 : 4);

    const showFo = toggles.foScore && foScoresArr.some(v => v != null);
    const showEq = toggles.eqScore && eqScoresArr.some(v => v != null);
    const margins = chartPaneMargins(showFo, showEq);

    chart.priceScale('right').applyOptions({ scaleMargins: margins.candle });

    const candleData = data.map(d => ({
      time: d.date,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
    }));
    candleSeries.setData(candleData);

    let foScoreSeries = null;
    let eqScoreSeries = null;

    if (showFo) {
      foScoreSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'price', precision: 0, minMove: 1 },
        priceScaleId: 'foScore',
        title: 'FO',
      });
      foScoreRef.current = foScoreSeries;
      chart.priceScale('foScore').applyOptions({
        scaleMargins: margins.fo,
        borderColor: 'rgba(42,46,57,0.5)',
        autoScale: true,
      });
      foScoreSeries.setData(buildScoreSeries(dates, foScoresArr, foOB, foOS));
    }

    if (showEq) {
      eqScoreSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'price', precision: 0, minMove: 1 },
        priceScaleId: 'eqScore',
        title: 'EQ',
      });
      eqScoreRef.current = eqScoreSeries;
      chart.priceScale('eqScore').applyOptions({
        scaleMargins: margins.eq,
        borderColor: 'rgba(42,46,57,0.5)',
        autoScale: true,
      });
      eqScoreSeries.setData(buildScoreSeries(dates, eqScoresArr, eqOB, eqOS));
    }

    // EMA overlays (computed from full history)
    for (const cfg of EMA_CFG) {
      if (!toggles[cfg.key]) continue;
      const emaFull = emaSafe(fullCloses, cfg.period);
      const emaVis = mapSeriesToVisible(fullDates, emaFull, dates);
      addLine(chart, dates.map((t, i) => ({ time: t, value: emaVis[i] })), {
        color: cfg.color, width: cfg.width, title: cfg.key.toUpperCase(),
      });
    }

    // FO overlays: SVWAP, PSVWAP
    if (foData?.length && (toggles.svwap || toggles.psvwap)) {
      const foMap = new Map();
      foData.forEach(r => foMap.set(r.date, r));
      if (toggles.svwap) {
        addLine(chart, dates.map(t => ({ time: t, value: foMap.get(t)?.svwap ?? null })), {
          color: '#FF9800', width: 2, dash: true, title: 'SVWAP',
        });
      }
      if (toggles.psvwap) {
        addLine(chart, dates.map(t => ({ time: t, value: foMap.get(t)?.psvwap ?? null })), {
          color: '#9C27B0', width: 2, title: 'PSVWAP',
        });
      }
    }

    // LRC
    if (toggles.lrc) {
      const lrc = linRegChannel(C);
      addLine(chart, dates.map((t, i) => ({ time: t, value: lrc.lr[i] })), { color: '#FFD54F', width: 1.8, title: 'LR' });
      addLine(chart, dates.map((t, i) => ({ time: t, value: lrc.ub[i] })), { color: 'rgba(255,213,79,0.55)', width: 1.2, dash: true, title: 'LR UB' });
      addLine(chart, dates.map((t, i) => ({ time: t, value: lrc.lb[i] })), { color: 'rgba(255,213,79,0.55)', width: 1.2, dash: true, title: 'LR LB' });
    }

    // ATR trailing stops
    if (toggles.atr217) {
      const tsl = atrTrailingStop(H, L, C, 21, 7);
      addLine(chart, dates.map((t, i) => ({ time: t, value: tsl[i] })), { color: 'rgba(76,175,80,0.85)', width: 1.6, dash: true, title: 'ATR21×7' });
    }
    if (toggles.atr103) {
      const tsl = atrTrailingStop(H, L, C, 10, 3);
      addLine(chart, dates.map((t, i) => ({ time: t, value: tsl[i] })), { color: 'rgba(244,67,54,0.85)', width: 1.6, dash: true, title: 'ATR10×3' });
    }

    // CPR segment lines
    if (toggles.cprM && cprMonthly?.length) {
      const segs = buildCPRSegments(cprMonthly, dates);
      addSegmentLines(chart, segs, dates, ['pivot', 'tc', 'bc', 'r1', 's1', 'r2', 's2', 'r3', 's3', 'ph', 'pl'], '#E53935');
    }
    if (toggles.cprW && cprWeekly?.length) {
      const segs = buildCPRSegments(cprWeekly, dates);
      addSegmentLines(chart, segs, dates, ['pivot', 'tc', 'bc', 'r1', 's1', 'r2', 's2', 'r3', 's3', 'ph', 'pl'], '#1565C0');
    }

    // FIB price lines
    if (toggles.fib) {
      const fib = computeFibLevels(H, L, n);
      if (fib) {
        fib.levels.forEach((lv, i) => {
          const pl = candleSeries.createPriceLine({
            price: lv.price,
            color: FIB_COLORS[i] || 'rgba(255,255,255,0.5)',
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: `${lv.k} ${lv.price.toFixed(2)}`,
          });
          priceLineRefs.current.push(pl);
        });
      }
    }

    // FO / EQ / participant signal markers — scaled to bar width (zoom + mobile)
    const applyMarkers = () => {
      if (!markersRef.current) return;
      const barSpacing = chart.timeScale().options().barSpacing;
      const markers = buildSignalMarkers({
        data, toggles, foData, eqData, foReversal, eqReversal, partSignals,
        barSpacing, containerWidth: el.clientWidth,
      });
      markersRef.current.setMarkers(markers);
    };

    const initialMarkers = buildSignalMarkers({
      data, toggles, foData, eqData, foReversal, eqReversal, partSignals,
      barSpacing: chart.timeScale().options().barSpacing,
      containerWidth: el.clientWidth,
    });
    markersRef.current = createSeriesMarkers(candleSeries, initialMarkers);

    const onMarkerScaleChange = () => applyMarkers();
    chart.timeScale().subscribeVisibleLogicalRangeChange(onMarkerScaleChange);

    // OHLCV legend
    if (!legendRef.current) {
      const legend = document.createElement('div');
      legend.style.cssText = 'position:absolute;top:8px;left:12px;z-index:10;font-size:11px;font-family:var(--font-mono,monospace);color:var(--text-legend,var(--text-muted));pointer-events:none;line-height:1.6;';
      el.style.position = 'relative';
      el.appendChild(legend);
      legendRef.current = legend;
    }

    // Floating zoom / pan toolbar (bottom-right)
    if (!zoomBarRef.current) {
      const bar = document.createElement('div');
      bar.className = 'ic-zoom-bar';
      bar.innerHTML = `
        <button type="button" data-act="zin" title="Zoom in (+)">+</button>
        <button type="button" data-act="zout" title="Zoom out (−)">−</button>
        <button type="button" data-act="left" title="Pan left (←)">‹</button>
        <button type="button" data-act="right" title="Pan right (→)">›</button>
        <button type="button" data-act="fit" title="Fit all (Home / 0)">⟲</button>
      `;
      bar.addEventListener('click', (ev) => {
        const btn = ev.target.closest('button[data-act]');
        if (!btn || !chartRef.current) return;
        ev.preventDefault();
        ev.stopPropagation();
        const act = btn.getAttribute('data-act');
        if (act === 'zin') zoomLogical(chartRef.current, 0.82, 0.5);
        else if (act === 'zout') zoomLogical(chartRef.current, 1.22, 0.5);
        else if (act === 'left') panLogical(chartRef.current, -8);
        else if (act === 'right') panLogical(chartRef.current, 8);
        else if (act === 'fit') {
          fitChart(chartRef.current);
          resetPriceScale(chartRef.current, 'right');
        }
      });
      // Prevent drag-pan starting from toolbar clicks
      bar.addEventListener('mousedown', (ev) => ev.stopPropagation());
      bar.addEventListener('touchstart', (ev) => ev.stopPropagation(), { passive: true });
      el.appendChild(bar);
      zoomBarRef.current = bar;
    }

    const fmtP = v => (v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }));

    chart.subscribeCrosshairMove(param => {
      if (!param.time || !param.seriesData) {
        if (legendRef.current) legendRef.current.innerHTML = '';
        if (onHoverIdx) onHoverIdx(-1);
        return;
      }
      const cd = param.seriesData.get(candleSeries);
      if (!cd) return;
      const idx = dates.indexOf(param.time);
      if (onHoverIdx) onHoverIdx(idx);
      const clr = cd.close >= cd.open ? '#26a69a' : '#ef5350';
      const foPt = foScoreSeries ? param.seriesData.get(foScoreSeries) : null;
      const eqPt = eqScoreSeries ? param.seriesData.get(eqScoreSeries) : null;
      let scoreHtml = '';
      if (foPt?.value != null) {
        scoreHtml += `<span style="color:#4b5563">FO</span><span style="color:#FF9800"> ${foPt.value}/20 </span>`;
      }
      if (eqPt?.value != null) {
        scoreHtml += `<span style="color:#4b5563">EQ</span><span style="color:#42A5F5"> ${eqPt.value}/${eqMaxScore} </span>`;
      }
      let partHtml = '';
      if (toggles.partSig && partSeriesByDate?.size && idx >= 0) {
        const prow = resolvePartRow(partSeriesByDate, data, idx);
        const chips = buildPartHoverHtml(prow, { compact: true });
        if (chips) {
          partHtml = `<div style="margin-top:1px;display:flex;flex-wrap:wrap;gap:6px;align-items:center">${chips}</div>`;
        }
      }
      if (legendRef.current) {
        legendRef.current.innerHTML =
          `<span style="color:#6b7280">O</span><span style="color:${clr}"> ${fmtP(cd.open)} </span>` +
          `<span style="color:#6b7280">H</span><span style="color:#26a69a"> ${fmtP(cd.high)} </span>` +
          `<span style="color:#6b7280">L</span><span style="color:#ef5350"> ${fmtP(cd.low)} </span>` +
          `<span style="color:#6b7280">C</span><span style="color:${clr}"> ${fmtP(cd.close)} </span>` +
          scoreHtml +
          partHtml;
      }
      if (onCrosshair && idx >= 0) onCrosshair(idx, data[idx]);
    });

    const ro = new ResizeObserver(entries => {
      if (entries[0] && chartRef.current) {
        chartRef.current.applyOptions({ width: entries[0].contentRect.width });
        applyMarkers();
      }
    });
    ro.observe(el);
    chart.timeScale().fitContent();
    applyMarkers();

    return () => {
      detachInteraction();
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onMarkerScaleChange);
      ro.disconnect();
      if (legendRef.current?.parentNode) {
        legendRef.current.parentNode.removeChild(legendRef.current);
        legendRef.current = null;
      }
      if (zoomBarRef.current?.parentNode) {
        zoomBarRef.current.parentNode.removeChild(zoomBarRef.current);
        zoomBarRef.current = null;
      }
      if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }
      candleRef.current = null;
      foScoreRef.current = null;
      eqScoreRef.current = null;
      markersRef.current = null;
      overlayRefs.current = [];
      priceLineRefs.current = [];
    };
  }, [
    data, fullData, toggles, foData, eqData, partSignals, partSeriesByDate, cprMonthly, cprWeekly,
    monthStarts, foReversal, eqReversal, height, onCrosshair, onHoverIdx,
    clearOverlays, addLine, addSegmentLines,
  ]);

  if (!data.length) return null;

  return (
    <div
      ref={containerRef}
      className="ic-chart-canvas"
      style={{ width: '100%', height }}
      title="Scroll=zoom · Shift+scroll=pan · Drag=pan · Pinch=zoom · Dbl-click=fit · +/− / ←→ / Home"
    />
  );
}